import { useEffect, useRef, useState } from "react";
import { Link, useForm } from "@inertiajs/react";
import Form from "@/Components/Form";
import TextField from "@/Components/TextField";
import Button from "@/Components/Button";
import Checkbox from "@/Components/Checkbox";
import GuestLayout from "@/Layouts/GuestLayout";
import { Strings } from "@/resources";
import eyeOffIcon from "../../../../images/icons/icon_Eyeoff.svg";
import eyeOnIcon from "../../../../images/icons/icon_Eyeon.svg";
import moment from "moment-timezone";
import AppHead from "@/Components/AppHead";
import { sanitizeHtml } from "@/Helpers/DraftEditor";
import ReCAPTCHA from "react-google-recaptcha";
import { getSpecificHumanReadableTime } from "@/utils/time";
import { determineLoginIdType } from "@/utils/input";
import { apiLogin } from "@/services/auth";
import { toast } from "react-toastify";
import './embedded-login.scss';
import TermsModal from "@/Components/Modals/Terms";

export default function Login({
    status,
    ziggy,
    app_logo,
    clinic,
    termsDescription,
    use_pdf,
    file_url,
    showIPCaptcha,
    showAccountCaptcha,
    app_version_number
}: {
    status?: string;
    ziggy: any;
    app_logo?: string;
    clinic?: any;
    termsDescription?: any;
    use_pdf?: boolean;
    file_url?: string;
    showIPCaptcha: boolean;
    showAccountCaptcha: boolean;
    app_version_number?: string;
}) {
    const language = Strings.accept_terms;
    const PasswordFieldRef: any = useRef(null);
    const recaptchaRef: any = useRef();
    const [timezone, setTimezone] = useState("");

    const [accountThrottle, setAccountThrottle] = useState(0);
    const [accountaThrottleHumanTime, setAccountThrottleHumanTime] =
        useState("");

    const [ipThrottle, setIpThrottle] = useState(0);
    const [ipThrottleHumanTime, setIpThrottleHumanTime] = useState("");
    interface FormData {
        email: string;
        phone_number: string;
        password: string;
        remember: boolean;
        timezone: string;
        terms_accepted: any;
        ip_throttle: any;
        account_throttle: any;
        recaptcha_token?: string;
    }

    const { data, setData, post, processing, errors, reset, transform } =
        useForm<FormData>({
            email: "",
            phone_number: "",
            password: "",
            remember: false,
            timezone: "",
            terms_accepted: "",
            account_throttle: "",
            ip_throttle: "",
            recaptcha_token: "",
        });
    transform((data) => ({
        ...data,
        timezone: timezone,
    }));

    const [manualErrors, setManualErrors] = useState<{
        email: null|string,
        phone_number: null|string,
        password: null|string,
        remember: null|string,
        timezone: null|string,
        terms_accepted: null|string,
        account_throttle: null|string,
        ip_throttle: null|string,
        recaptcha_token: null|string,
    }>({
        email: null,
        phone_number: null,
        password: null,
        remember: null,
        timezone: null,
        terms_accepted: null,
        account_throttle: null,
        ip_throttle: null,
        recaptcha_token: null,
    });
    const [loading, setLoading] = useState(false);

    const [inputFormat, setInputFormat] = useState("email");

    const phoneNumberCallback = (value: string) => {
        setData({
            ...data,
            phone_number: value,
            email: "",
        });
        setInputFormat("phone");
    };
    const emailCallback = (value: string) => {
        setData({
            ...data,
            email: value,
            phone_number: "",
        });
        setInputFormat("email");
    };

    const updateThrottleTime = () => {
        setAccountThrottle((prevThrottle) => {
            // Check if the previous throttle value is greater than 0
            if (prevThrottle > 0) {
                const newThrottle = prevThrottle - 1;
                // Update the human readable time only if the new throttle value is not 0
                setAccountThrottleHumanTime(
                    getSpecificHumanReadableTime(newThrottle),
                );
                return newThrottle;
            }
            // If the previous throttle value is 0, return it without changes
            return prevThrottle;
        });

        setIpThrottle((prevThrottle) => {
            // Check if the previous throttle value is greater than 0
            if (prevThrottle > 0) {
                const newThrottle = prevThrottle - 1;
                // Update the human readable time only if the new throttle value is not 0
                setIpThrottleHumanTime(
                    getSpecificHumanReadableTime(newThrottle),
                );
                return newThrottle;
            }
            // If the previous throttle value is 0, return it without changes
            return prevThrottle;
        });
    };

    useEffect(() => {
        let timezoneGuess = moment.tz.guess();
        timezoneGuess = timezoneGuess.replace("Calcutta", "Kolkata");
        setTimezone(timezoneGuess);
        initializeEmail();
        const timer = setInterval(() => {
            updateThrottleTime();
        }, 1000);
        return () => {
            reset("password");
            clearInterval(timer);
        };
    }, []);

    const initializeEmail = () => {
        const queryString = window.location.search;
        const searchParams = new URLSearchParams(queryString);
        const email = searchParams.get("email");
        const phoneNumber = searchParams.get("phone_number");

        if (email) {
            setData("email", email);
            setInputFormat("email");
            PasswordFieldRef?.current && PasswordFieldRef.current.focus();
        } else if (phoneNumber) {
            setData("phone_number", phoneNumber);
            setInputFormat("phone");
            PasswordFieldRef?.current && PasswordFieldRef.current.focus();
        }
    };

    const submit = async () => {
        setLoading(true);
        await apiLogin(data, ziggy.clinic).then((response) => {
            setLoading(false);
            if (response.errors) {
                setManualErrors({
                  ...manualErrors,
                  ...response.errors,
                });
                toast.error(response.message);
                if (response?.errors?.account_throttle) {
                    setAccountThrottle(Number(response?.errors?.account_throttle));
                    setAccountThrottleHumanTime("");
                }
                if (response?.errors?.ip_throttle) {
                    setIpThrottle(Number(response?.errors?.ip_throttle));
                    setIpThrottleHumanTime("");
                }
                recaptchaRef?.current && recaptchaRef?.current?.reset();
            } else {
                recaptchaRef?.current && recaptchaRef?.current?.reset();

                openLinkInNewTab(response.timed_url);
                reset();

                setManualErrors({
                    email: null,
                    phone_number: null,
                    password: null,
                    remember: null,
                    timezone: null,
                    terms_accepted: null,
                    account_throttle: null,
                    ip_throttle: null,
                    recaptcha_token: null,
                });
            }
        });
    };

    const openLinkInNewTab = (url: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }

    function recaptchaUpdated(value: any) {
        setData("recaptcha_token", value);
    }
    const renderCaptcha = () => {
        if (!showIPCaptcha && !showAccountCaptcha) return null;
        return (
            <div className="captcha-con">
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={import.meta.env.VITE_CAPTCHA_KEY}
                    onErrored={() => setData("recaptcha_token", "")}
                    onChange={recaptchaUpdated}
                />
            </div>
        );
    };

    const renderCaptchaError = () => {
        if (!manualErrors.recaptcha_token) return null;
        return (
            <div className="invalid-attempt show">{manualErrors.recaptcha_token}</div>
        );
    };

    const renderIPThrottleError = () => {
        if (!ipThrottle || ipThrottleHumanTime === "") return null;
        return (
            <div className="invalid-attempt show">
                IP blocked, Please retry in {ipThrottleHumanTime}
                {"."}
            </div>
        );
    };
    const renderAccountThrottleError = () => {
        if (!accountThrottle || accountaThrottleHumanTime === "") return null;
        return (
            <div className="invalid-attempt show">
                Account blocked, Please retry in {accountaThrottleHumanTime}
                {"."}
            </div>
        );
    };

    const userIcon = (
        <span className="svg-img-replacer left-icon">
            <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <g clipPath="url(#clip0_1_12122)">
                    <path
                        d="M12 11.1719C14.2091 11.1719 16 9.38101 16 7.17188C16 4.96274 14.2091 3.17188 12 3.17188C9.79086 3.17188 8 4.96274 8 7.17188C8 9.38101 9.79086 11.1719 12 11.1719Z"
                        stroke="var(--secondary-color)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                    <path
                        d="M6 21.1719V19.1719C6 18.111 6.42143 17.0936 7.17157 16.3434C7.92172 15.5933 8.93913 15.1719 10 15.1719H14C15.0609 15.1719 16.0783 15.5933 16.8284 16.3434C17.5786 17.0936 18 18.111 18 19.1719V21.1719"
                        stroke="var(--primary-color)"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    />
                </g>
                <defs>
                    <clipPath id="clip0_1_12122">
                        <rect
                            width="24"
                            height="24"
                            fill="white"
                            transform="translate(0 0.171875)"
                        />
                    </clipPath>
                </defs>
            </svg>
        </span>
    );

    const passwordIcon = (
        <div className="left-icon svg-img-replacer">
            <svg
                width="24"
                height="25"
                viewBox="0 0 24 25"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
            >
                <path
                    d="M17 22.1719H7C3 22.1719 2 21.1719 2 17.1719V15.1719C2 11.1719 3 10.1719 7 10.1719H17C21 10.1719 22 11.1719 22 15.1719V17.1719C22 21.1719 21 22.1719 17 22.1719Z"
                    stroke="var(--primary-color)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M6 10.1719V8.17188C6 4.86188 7 2.17188 12 2.17188C16.5 2.17188 18 4.17188 18 7.17188"
                    stroke="var(--primary-color)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
                <path
                    d="M12 18.6719C13.3807 18.6719 14.5 17.5526 14.5 16.1719C14.5 14.7912 13.3807 13.6719 12 13.6719C10.6193 13.6719 9.5 14.7912 9.5 16.1719C9.5 17.5526 10.6193 18.6719 12 18.6719Z"
                    stroke="var(--secondary-color)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            </svg>
        </div>
    );

    const renderVersionNumber = () => {
        return (
            <div className='version-number'>
              <span className='version-number-text'>
                {app_version_number}
              </span>
            </div>
          )
    }

    return (
        <main className="login form-screens">
            <AppHead clinic={clinic} title={Strings.log_in} />

            <Form
                onSubmit={submit}
                status={status}
                title={Strings.log_in}
                app_logo={app_logo}
                className="login-form form-container"
                ziggy={ziggy}
                openLinksInNewTab={true}
            >
                <div className="input-container">
                    <div className="input-fieldContent">
                        <TextField
                            type=""
                            name="email"
                            value={
                                inputFormat === "phone"
                                    ? data.phone_number
                                    : data.email
                            }
                            placeholder={Strings.email_or_phone}
                            isFocused={true}
                            leftIcon={userIcon}
                            error={manualErrors.email}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) =>
                                determineLoginIdType(
                                    e.target.value,
                                    phoneNumberCallback,
                                    emailCallback,
                                )
                            }
                        />
                    </div>
                    <div className="input-fieldContent">
                        <TextField
                            type="password"
                            name="password"
                            value={data.password}
                            placeholder={Strings.password}
                            leftIcon={passwordIcon}
                            rightIcon={eyeOffIcon}
                            rightIcon2={eyeOnIcon}
                            error={manualErrors.password}
                            ref={PasswordFieldRef}
                            onChange={(
                                e: React.ChangeEvent<HTMLInputElement>,
                            ) => setData("password", e.target.value)}
                        />
                        <Link
                            href="#"
                            onClick={(e: any) => openLinkInNewTab(route("password.request", {
                                clinic: ziggy.clinic,
                            }))}
                            tabIndex={-1}
                            className="forget"
                        >
                            {Strings.forgot_password}
                        </Link>
                    </div>
                </div>
                {renderCaptcha()}
                {renderCaptchaError()}
                {renderIPThrottleError()}
                {renderAccountThrottleError()}
                <div className="form-btn">
                    <Button
                        disabled={loading}
                        loading={loading}
                    >
                        <p>{Strings.sign_in}</p>
                    </Button>
                </div>

                <p className="form-bottomLine p-sm">
                    {Strings.not_registered}{" "}
                    <Link
                        href="#"
                        onClick={(_e: any) => openLinkInNewTab(route("signup", { clinic: ziggy.clinic }))}
                        tabIndex={-1}
                    >
                        {Strings.create_account}
                    </Link>
                </p>
                {app_version_number ? renderVersionNumber() : null}
            </Form>

            <TermsModal
                show={data.terms_accepted === "" && Object.values(manualErrors).find((val: any) => val !== null)?.length === 1 && manualErrors.terms_accepted !== null}
                description={termsDescription}
                use_pdf={use_pdf}
                file_url={file_url}
                setTermsAccepted={(val: boolean) => {
                    setData("terms_accepted", val);
                    transform((data) => ({
                        ...data,
                        terms_accepted: val,
                    }));
                }}
                submit={submit}
                openLinkInNewTab={openLinkInNewTab}
                isUpdate
            />
            
        </main>
    );
}
